import { Button } from '@paulownia/shared/components/ui/button';
import { Label } from '@paulownia/shared/components/ui/label';
import { Plus, Minus } from 'lucide-react';
import { type Product, getEffectiveSaleMode, getPortionPrice, safeToFixed } from '@paulownia/shared/types';
import { ServingSelector } from './ServingSelector';

interface QuantitySelectorProps {
    product: Product;
    quantity: number;
    onQuantityChange: (quantity: number) => void;
    // For WEIGHT_ESTIMATED mode
    servings: number;
    onServingsChange: (servings: number) => void;
    // For WEIGHT_PACKAGED mode
    selectedPortionSizeId: string | null;
    onPortionSizeChange: (portionSizeId: string) => void;
}


/**
 * Adaptive quantity selector that renders different UIs based on sale mode:
 * - UNIT, UNIT_PRICED_BY_WEIGHT: Integer stepper (– 1 +)
 * - WEIGHT: Decimal stepper with kg display
 * - WEIGHT_ESTIMATED: Serving buttons (2, 4, 6, 8 pers)
 * - WEIGHT_PACKAGED: Portion buttons + integer stepper
 */
export function QuantitySelector({
    product,
    quantity,
    onQuantityChange,
    servings,
    onServingsChange,
    selectedPortionSizeId,
    onPortionSizeChange,
}: QuantitySelectorProps) {
    const saleMode = getEffectiveSaleMode(product);

    // Stepper component for integer/decimal quantities
    const renderStepper = (step: number, isKg: boolean) => {
        const handleChange = (delta: number) => {
            const newQuantity = Math.max(0, Math.round((quantity + delta * step) * 1000) / 1000);
            onQuantityChange(newQuantity);
        };

        return (
            <div className="space-y-3">
                <Label className="text-base font-semibold">Quantité</Label>
                <div className="flex items-center justify-center gap-4">
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleChange(-1)}
                        disabled={quantity <= 0}
                        className="w-10 h-10"
                    >
                        <Minus className="w-4 h-4" />
                    </Button>
                    <div className="min-w-[80px] text-center">
                        <span className="text-2xl font-bold text-gray-900">
                            {isKg ? safeToFixed(quantity, 3) : quantity}
                        </span>
                        {isKg && (
                            <span className="text-sm text-gray-500 ml-1">kg</span>
                        )}
                    </div>
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleChange(1)}
                        className="w-10 h-10"
                    >
                        <Plus className="w-4 h-4" />
                    </Button>
                </div>
            </div>
        );
    };

    // Portion size selection for WEIGHT_PACKAGED
    const renderPortionSelector = () => {
        const portionSizes = product.portionSizes || [];
        if (portionSizes.length === 0) return null;

        return (
            <div className="space-y-3">
                <Label className="text-base font-semibold">Format</Label>
                <div className="grid grid-cols-1 gap-2">
                    {portionSizes.map((portion) => {
                        const portionPrice = getPortionPrice(product, portion.id);
                        const isSelected = selectedPortionSizeId === portion.id;
                        return (
                            <button
                                key={portion.id}
                                type="button"
                                onClick={() => onPortionSizeChange(portion.id)}
                                className={`flex items-center justify-between p-3 rounded-lg border-2 transition-colors ${
                                    isSelected
                                        ? 'border-orange-500 bg-orange-50'
                                        : 'border-gray-200 hover:border-gray-300'
                                }`}
                            >
                                <span className={`font-medium ${isSelected ? 'text-orange-700' : 'text-gray-900'}`}>
                                    {portion.label}
                                </span>
                                {portionPrice !== null && Number.isFinite(portionPrice) && (
                                    <span className={`font-bold ${isSelected ? 'text-orange-700' : 'text-gray-700'}`}>
                                        {safeToFixed(portionPrice, 2)}€
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    switch (saleMode) {
        case 'UNIT':
        case 'UNIT_PRICED_BY_WEIGHT':
            // Integer stepper (step = 1)
            return renderStepper(1, false);

        case 'WEIGHT':
            // Decimal stepper (step = 0.1 kg)
            return renderStepper(0.1, true);

        case 'WEIGHT_ESTIMATED':
            // Serving selector + display calculated weight
            return (
                <div className="space-y-4">
                    <ServingSelector
                        servings={servings}
                        onServingsChange={onServingsChange}
                    />
                    {/* Show calculated weight estimate */}
                    {product.servingEstimate?.gramsPerPerson && servings > 0 && (
                        <div className="text-center text-sm text-gray-500">
                            ≈ {safeToFixed((product.servingEstimate.gramsPerPerson * servings) / 1000, 2).replace('.', ',')} kg
                        </div>
                    )}
                </div>
            );

        case 'WEIGHT_PACKAGED':
            // Portion buttons + integer stepper for quantity
            return (
                <div className="space-y-4">
                    {renderPortionSelector()}
                    {selectedPortionSizeId && (
                        <div className="space-y-3 pt-2 border-t">
                            {renderStepper(1, false)}
                        </div>
                    )}
                </div>
            );

        default:
            // Fallback to unit stepper
            return renderStepper(1, false);
    }
}
